var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.intrepid",
  title: "EGT Intrepid page",
  description: "Intrepid — the AI partner. It proposes; you decide; nothing happens until you say yes.",
  supportServer: true,
  fields: { reserveUrl: { title: "Reserve URL", type: "uri", description: "Reservation entry point.", required: false } }
});
var S = require("../helpers/egt.sections.js");

/* =====================================================================
 * INTREPID — "A presence with moods" (its OWN world).
 * A calm, breathing intelligence you can step through mood by mood — and
 * watch it propose, never act. Distinct from the Platform page: the hero is
 * a bespoke LIVING PRESENCE (CSS/SVG breathing avatar, not a photograph),
 * the spine is a MOOD JOURNEY (a mood switcher recolors the whole scene via
 * CSS vars), and the demo is a believable scripted CHAT that ends on the
 * real product's gesture — it proposes -> you decide -> nothing happens
 * until you say yes. Frosted glass over near-black; one gold through-line;
 * mood accents may use restrained color. Says WHAT + WHY, never HOW.
 *
 * Honesty discipline: every demo is illustrative; no backend; no dates;
 * the banned posture word never appears (Intrepid "prepares"; nothing is sent).
 * ===================================================================== */

/* ---- The RANGE of states (WP-N5.4) — the four LIVING states of the husky
 * avatar, shown side-by-side as a "contact sheet" so a visitor can see his
 * full range at a glance. Each key is a real rig mood the shared rig honors
 * (ready / focused / briefing / dreaming) — the moodJourney mounts one husky
 * per state via [data-egt-rig-mount][data-egt-rig-mood]. WHAT + WHY, not HOW. */
var RANGE = [
  { key: "ready",    label: "Ready",
    line: "On watch. Learning your business, waiting for the word." },
  { key: "focused",  label: "Focused",
    line: "On task. Tightening a headline, shaping a page — in your voice, to the point." },
  { key: "briefing", label: "Briefing",
    line: "The morning briefing: a few good moves and one good question, ready when you wake." },
  { key: "dreaming", label: "Dreaming",
    line: "Off the leash, gently. Ideating moves you might not have asked for yet." }
];

/* The scripted CHAT transcript (S4) — a believable morning of real project
 * work: he reads the store, checks inventory, finds a supplier price drop,
 * and drafts a plan. Fully server-rendered so it's readable + crawlable with
 * JS off; the client re-plays it one message at a time (thinking pause ->
 * typing indicator -> typewriter reveal) while the avatar animates from
 * FOCUSED (working) to BRIEFING (presenting). Fake data — it shows the
 * GESTURE (research -> review -> propose -> you decide), never the HOW. */
var CHAT = [
  { who: "you",      text: "Morning — anything I should look at on the store?" },
  { who: "intrepid", text: "On it — reading last week's orders now." },
  { who: "intrepid", text: "Five items are running low and two sold out over the weekend." },
  { who: "intrepid", text: "Checking your suppliers — one just dropped the price on your bestseller." },
  { who: "intrepid", text: "I've put together a restock plan for all five, at the better price. Nothing's ordered." }
];

/* The proposal cards the chat resolves into (S4). Stable hooks
 * (data-egt-demo) let the client bind, approve/dismiss/undo, and rotate. */
var DEMO_CARDS = [
  {
    badge: "Overnight · Ready for you",
    title: "Restock five items before the weekend rush.",
    body: "I sized the quantities from last week's sell-through and locked the lower supplier price. Nothing is ordered.",
    diff: { was: "Bestseller — 0 in stock", now: "Restock 48 · price −12%" },
    queued: "Ready to order — you decide."
  },
  {
    badge: "Overnight · Ready for you",
    title: "Your pricing page hasn't changed in 5 months.",
    body: "I prepared a tighter headline and a clearer plan order. Your live page is untouched.",
    diff: { was: "Plans & Pricing", now: "Simple plans. No surprises." },
    queued: "Staged — you can still undo."
  },
  {
    badge: "Overnight · Ready for you",
    title: "A reviewer asked a question you haven't answered.",
    body: "I prepared a warm, on-brand reply for you to read. It will not post itself.",
    diff: { was: "Re: your question —", now: "Great question — here's exactly how it works." },
    queued: "Ready to send — you decide." }
];

exports.render = async function render(options) {
  var reserve = (options && options.reserveUrl) || "/reserve";
  return S.page(reserve, [
    intrepidSchema(),
    presenceHero(reserve),
    presenceCapture(),
    spine(),
    moodJourney(),
    chatDemo(),
    whoSteers(),
    comingClose(reserve)
  ], { betaCta: true, footerCaptureLabel: S.FOOTER_LABELS.platform });
};

/* Per-surface JSON-LD: WebPage + BreadcrumbList for /intrepid. Site-wide
 * Organization + WebSite come from S.page. URLs from S.siteOrigin(). No
 * product/offer type — Intrepid is a private-beta AI partner with no public
 * pricing or reviews (no fabricated facts). */
function intrepidSchema() {
  var O = S.siteOrigin();
  return S.jsonLd([
    { "@type": "WebPage", "@id": O + "/intrepid#webpage", url: O + "/intrepid",
      name: "Intrepid — the AI partner",
      isPartOf: { "@id": O + "/#website" },
      about: { "@id": O + "/intrepid#app" },
      breadcrumb: { "@id": O + "/intrepid#breadcrumb" } },
    /* Intrepid as its own SoftwareApplication, part of the Universe Platform
     * (SEO wave 2B-8, 2026-10-09). Description = the hero lead, verbatim. No
     * offers, ratings, dates or availability claims beyond what the page says. */
    { "@type": "SoftwareApplication", "@id": O + "/intrepid#app",
      name: "Intrepid", alternateName: "Intrepid by EGT",
      url: O + "/intrepid",
      applicationCategory: "BusinessApplication",
      description: "Not a chatbot — a partner steered by your brand, your data, and your goals. It proposes. You decide.",
      isPartOf: { "@id": O + "/platform#app" },
      publisher: { "@id": O + "/#organization" } },
    { "@type": "BreadcrumbList", "@id": O + "/intrepid#breadcrumb",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: O + "/" },
        { "@type": "ListItem", position: 2, name: "Intrepid" }
      ] }
  ]);
}

/* S1 — PRESENCE HERO. Near-black; the Living Presence breathes center/low.
 * One frosted card with the locked-ish hero line + Reserve. The presence is
 * CSS/SVG (cheap) so it doesn't fight the image budget. The whole hero is a
 * mood root (data-mood) so the breath/color reflect the journey default. */
function presenceHero(reserve) {
  return {
    n: "section", c: "egt-canvas egt-intrepid-hero", "data-mood": "focused",
    i: [
      { n: "div", c: "egt__shell egt-canvas__inner egt-intrepid-hero__inner",
        i: [
          { n: "div", c: "egt-glass egt-glass--wide egt-intrepid-hero__card egt-reveal egt-reveal--up",
            i: [
              /* the living husky avatar on the right IS the page's mark now — no
               * separate small static lockup here (WP-N5.2). The trademark
               * license covers the DESIGN mark only (Schedule A: "no literal/
               * word element") — the ™ belongs on that visual mark (the rig,
               * below), not on the word "Intrepid" here. */
              { n: "p", c: "egt__eyebrow egt-reveal", style: "--egt-i:0", i: "Intrepid" },
              { n: "h1", c: "egt-intrepid-hero__title egt-reveal", style: "--egt-i:1",
                i: [ "A partner that thinks ", { n: "br" }, "while you ",
                  { n: "span", c: "egt__gold", i: "sleep." } ] },
              { n: "p", c: "egt-intrepid-hero__lead egt-reveal", style: "--egt-i:2",
                i: "Not a chatbot — a partner steered by your brand, your data, and your goals. It proposes. You decide." },
              /* answer-first (SEO wave 2C-5): what Intrepid is and who it's for */
              { n: "p", c: "egt-intrepid-hero__answer egt-reveal", style: "--egt-i:2",
                i: "Intrepid by EGT is the AI partner inside the Universe Platform. It proposes campaigns, replies and pages for you to approve — for owners who want the help without handing over the keys. It's part of the public beta." },
              /* WP-N5.1 — friendly-helper framing first; the interactivity is a
               * warm aside, not the headline. Meaningful now, so not aria-hidden. */
              { n: "p", c: "egt-intrepid-hero__hint egt-reveal", style: "--egt-i:3",
                i: [ "Say hello — he's a friendly companion from the book ",
                  { n: "span", c: "egt__gold", i: "Exploit" },
                  ", here to help you carry your project. His eyes follow your cursor — give him a wave." ] },
              { n: "div", c: "egt-intrepid-hero__actions egt-reveal", style: "--egt-i:4",
                i: S.cta(reserve, "Join the Beta", true) }
            ] },
          /* the LIVING PRESENCE — the interactive Intrepid husky rig, mounted
           * client-side (egt.intrepid.js). NOT aria-hidden and NOT
           * pointer-events:none: this is the page's signature interactive
           * moment. rig.js swaps the OS cursor for the custom arrow ONLY while
           * the pointer is over him. Falls back to nothing with JS off. */
          { n: "div", c: "egt-intrepid-hero__stage egt-intrepid-rig",
            i: [
              { n: "div", c: "egt-intrepid-rig__mount",
                "data-egt-rig-mount": "true", "data-egt-rig-mood": "ready",
                role: "img", "aria-label": "Intrepid — a living husky that follows your cursor" },
              /* Unregistered design mark (USPTO Serial No. 99940706,
               * intent-to-use, filed but not yet examined) — ™ only, never
               * ®, per the trademark license (§8.1: no ® until a
               * registration actually issues). Placed on the visual mark
               * itself (this living avatar IS the mark, per the comment
               * above), not on the word "Intrepid". */
              { n: "span", c: "egt-intrepid-rig__tm", "aria-hidden": "true", i: "™" }
            ] }
        ] },
      { n: "a", href: "#egt-intrepid-spine", c: "egt-intrepid-hero__cue", "aria-label": "Scroll", i: "⌄" }
    ]
  };
}

/* S1b — CAPTURE (F23). Right after the presence hero, before the spine: take
 * the address while Intrepid is still on screen. Light on copy — an eyebrow,
 * one line, the inline field. source "intrepid" → the platform newsletter
 * (egt.capture.js SOURCES; Intrepid is a Platform product). Frosted panel over
 * the near-black page, undimmed (agents.md §4 — no scrim). Consent tick
 * REQUIRED + never pre-checked, same as home/voice/album. */
function presenceCapture() {
  return {
    n: "section", c: "egt-canvas egt-canvas--center egt-intrepid-capture", id: "egt-intrepid-capture",
    i: { n: "div", c: "egt__shell egt-canvas__inner egt-reveal",
      i: S.capture({
        source: "intrepid",
        id: "egt-cap-intrepid",
        variant: "panel",
        eyebrow: "Intrepid",
        heading: "h2",
        title: [ "Meet him ", { n: "span", c: "egt__gold", i: "first." } ],
        lead: "We'll tell you the moment Intrepid opens, along with letters about building EGT, the films and the Universe. We'll email you a code to confirm.",
        label: "Your email address",
        cta: "Notify me",
        consent: true
      }) }
  };
}

/* S2 — THE SPINE. The load-bearing sentence over a still dawn horizon.
 * Three clauses reveal in sequence. KEPT near-verbatim — it is the IP-safe
 * product truth + the emotional core. */
function spine() {
  var clauses = [ "It proposes.", "You decide.", "Nothing happens until you say yes." ];
  return {
    n: "section", id: "egt-intrepid-spine", c: "egt-canvas egt-canvas--tall egt-canvas--center egt-intrepid-spine",
    i: [
      S.media({ img: "/imgs/intrepid-dawn-horizon.jpg", alt: "", parallax: 0.1 }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-intrepid-spine__inner",
        i: { n: "div", c: "egt-glass egt-glass--light egt-intrepid-spine__card",
          i: [
            { n: "p", c: "egt__eyebrow egt-reveal", i: "The one principle" },
            { n: "p", c: "egt-intrepid-spine__line",
              i: clauses.map(function (t, i2) {
                return { n: "span", c: "egt-intrepid-spine__clause egt-reveal",
                  style: "--egt-i:" + i2,
                  i: (i2 === 2 ? [ { n: "span", c: "egt__gold", i: t } ] : [ t ]) };
              }) }
          ] } }
    ]
  };
}

/* S3 — ITS RANGE (WP-N5.6). A mood SELECTOR: one living husky, and four
 * labelled buttons (ready / focused / briefing / dreaming) that switch which
 * mood he's showing. Replaces the earlier "contact sheet" (all four states
 * mounted + animating simultaneously) — four concurrent rigs each running
 * dozens of CSS animations proved too heavy for Chrome. One animated husky
 * at a time is materially cheaper and reads just as strong: pick a state,
 * watch him actually be in it.
 *
 * PROGRESSIVE ENHANCEMENT: the labels + the first mood's line are server-
 * rendered and fully readable with JS off; the husky mount + selector
 * interactivity are the enhancement (client: initMoodSelector in
 * /src/egt.intrepid.js). Reduced motion: the rig CSS freezes him to a
 * static pose per state regardless of which is selected. */
function moodJourney() {
  var first = RANGE[0];
  return {
    n: "section", c: "egt-canvas egt-intrepid-moods",
    i: { n: "div", c: "egt__shell egt-canvas__inner egt-intrepid-moods__inner",
      i: [
        { n: "div", c: "egt-intrepid-moods__head egt-reveal egt-reveal--up",
          i: [
            { n: "p", c: "egt__eyebrow", i: "Its range" },
            { n: "h2", c: "egt-section__title", i: "One partner. A range of states." },
            { n: "p", c: "egt-section__lead",
              i: "Intrepid is never quite still. It moves between states as the work asks — on watch, heads-down, briefing you at dawn, or turning over ideas of its own. Pick a state — watch him actually be in it." }
          ] },
        { n: "div", c: "egt-intrepid-picker egt-reveal egt-reveal--up", "data-egt-mood-picker": "true",
          i: [
            { n: "div", c: "egt-intrepid-picker__stage egt-intrepid-rig",
              i: { n: "div", c: "egt-intrepid-rig__mount",
                "data-egt-rig-mount": "true", "data-egt-rig-mood": first.key,
                role: "img", "aria-label": "Intrepid — " + first.label } },
            { n: "div", c: "egt-intrepid-picker__panel",
              i: [
                { n: "div", c: "egt-intrepid-picker__tabs", role: "tablist", "aria-label": "Intrepid's mood",
                  i: RANGE.map(function (m, i2) {
                    return { n: "button", type: "button", c: "egt-intrepid-picker__tab",
                      role: "tab", "aria-selected": i2 === 0 ? "true" : "false",
                      "data-egt-mood-tab": m.key, "data-egt-mood-line": m.line, i: m.label };
                  }) },
                { n: "p", c: "egt-intrepid-picker__line", "data-egt-mood-line-display": "true", i: first.line }
              ] }
          ] }
      ] }
  };
}

/* =====================================================================
 * S4 — THE CHAT DEMO. Intrepid's own world made literal: a frosted chat
 * surface where a short propose/decide exchange plays out, ending on the
 * proposal card the visitor can Approve / Dismiss / Undo (the existing
 * state machine, re-homed here — reused, not rewritten).
 *
 * The full transcript + the first card are SERVER-RENDERED (crawlable,
 * works JS-off, accessible). The client replays the bubbles with a
 * thinking pause -> typing indicator -> typewriter reveal, then operates
 * the card. Permanent honest disclaimer; no backend; no dates.
 * ===================================================================== */
function chatDemo() {
  return {
    n: "section", c: "egt-canvas egt-intrepid-chat", "data-mood": "briefing",
    i: { n: "div", c: "egt__shell egt-canvas__inner egt-intrepid-chat__inner",
      i: [
        { n: "div", c: "egt-intrepid-chat__head egt-reveal",
          i: [
            { n: "p", c: "egt__eyebrow", i: "A morning with Intrepid" },
            { n: "h2", c: "egt-section__title", i: "Watch it work. Then you decide." },
            { n: "p", c: "egt-section__lead",
              i: "It reads the store, checks what's running low, and lines up the move — then leaves it for you to approve. Nothing happens until you say yes." }
          ] },
        chatStage()
      ] }
  };
}

function chatStage() {
  var first = DEMO_CARDS[0];
  return {
    n: "div", c: "egt-intrepid-chat__stage", "data-egt-demo": "root",
    i: [
      /* aria-live status region — announces typing + approve/undo for SR users */
      { n: "p", c: "egt-intrepid-chat__status", "data-egt-demo": "status",
        role: "status", "aria-live": "polite", i: "" },
      { n: "div", c: "egt-intrepid-chat__window",
        i: [
          { n: "div", c: "egt-intrepid-chat__bar",
            i: [
              /* the LIVING avatar — the same husky, mounted here in the chat's
               * own header bar (a real chat app's avatar-next-to-name), driven
               * by the client to animate its state as the demo runs: FOCUSED
               * while it works, BRIEFING when it presents the plan, back to
               * READY once approved. With JS off it's simply absent (the
               * transcript still reads). */
              { n: "div", c: "egt-intrepid-chat__avatar-stage egt-intrepid-rig",
                i: { n: "div", c: "egt-intrepid-rig__mount",
                  "data-egt-rig-mount": "true", "data-egt-rig-mood": "ready",
                  "data-egt-chat-avatar": "true",
                  role: "img", "aria-label": "Intrepid" } },
              { n: "div", c: "egt-intrepid-chat__bar-id",
                i: [
                  { n: "span", c: "egt-intrepid-chat__name", i: "Intrepid" },
                  { n: "span", c: "egt-intrepid-chat__avatar-state", "data-egt-demo": "avatarState",
                    "aria-hidden": "true", i: "On watch" }
                ] },
              { n: "span", c: "egt-intrepid-chat__live", "data-egt-demo": "typing", "aria-hidden": "true",
                i: [ { n: "span", c: "egt-intrepid-chat__dotp" },
                  { n: "span", c: "egt-intrepid-chat__dotp" },
                  { n: "span", c: "egt-intrepid-chat__dotp" } ] }
            ] },
          /* the transcript — fully server-rendered (JS-off readable). The
           * proposal card lives INSIDE this same scrollable flow (not as a
           * separate container below it) so it bounces in as the chat's own
           * final "message" using the exact same is-pending/is-in reveal,
           * instead of reading as a bolted-on second box. */
          { n: "div", c: "egt-intrepid-chat__log", "data-egt-demo": "log",
            i: CHAT.map(function (msg, i2) {
              return { n: "div",
                c: "egt-intrepid-chat__msg egt-intrepid-chat__msg--" + msg.who,
                "data-egt-chat-msg": String(i2),
                i: [
                  { n: "span", c: "egt-intrepid-chat__who",
                    i: (msg.who === "you" ? "You" : "Intrepid") },
                  { n: "p", c: "egt-intrepid-chat__bubble", "data-egt-chat-text": msg.text, i: msg.text }
                ] };
            }).concat([ whyCard(first) ]) }
        ] },
      { n: "div", c: "egt-intrepid-chat__foot",
        i: [
          { n: "p", c: "egt-intrepid-chat__caption", "data-egt-demo": "caption",
            i: "Illustrative · sample data, not a live agent" },
          /* replay the scripted run — enhancement only; hidden with JS off */
          { n: "button", type: "button", c: "egt-intrepid-chat__replay",
            "data-egt-demo": "replay", hidden: "hidden",
            i: [ { n: "span", c: "egt-intrepid-chat__replay-icon", "aria-hidden": "true", i: "↻" },
              " Replay" ] }
        ] }
    ]
  };
}

/* The proposal (Why-)card. Stable hooks (data-egt-demo) for the client to
 * bind, approve/dismiss/undo, and swap content between scripted cards. */
function whyCard(card) {
  return {
    n: "article", c: "egt-intrepid-card", "data-egt-demo": "card",
    "aria-label": "Proposed by Intrepid",
    i: [
      { n: "div", c: "egt-intrepid-card__top",
        i: [
          { n: "span", c: "egt-intrepid-card__badge", "data-egt-demo": "badge", i: card.badge },
          { n: "span", c: "egt-intrepid-card__dot", "aria-hidden": "true" }
        ] },
      { n: "h3", c: "egt-intrepid-card__title", "data-egt-demo": "title", i: card.title },
      { n: "p", c: "egt-intrepid-card__body", "data-egt-demo": "body", i: card.body },
      /* before -> after preview (the "show, then believe" 2-line diff) */
      { n: "div", c: "egt-intrepid-card__diff", "data-egt-demo": "diff",
        i: [
          { n: "p", c: "egt-intrepid-card__diff-row egt-intrepid-card__diff-row--was",
            i: [ { n: "span", c: "egt-intrepid-card__diff-mark", "aria-hidden": "true", i: "—" },
              { n: "span", "data-egt-demo": "diffWas", i: card.diff.was } ] },
          { n: "p", c: "egt-intrepid-card__diff-row egt-intrepid-card__diff-row--now",
            i: [ { n: "span", c: "egt-intrepid-card__diff-mark", "aria-hidden": "true", i: "+" },
              { n: "span", "data-egt-demo": "diffNow", i: card.diff.now } ] }
        ] },
      /* resolved state: a check that draws on + confirmation line (hidden until approve) */
      { n: "div", c: "egt-intrepid-card__resolved", "data-egt-demo": "resolved", "aria-hidden": "true",
        i: [
          { n: "svg", c: "egt-intrepid-card__check", viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false",
            i: { n: "path", c: "egt-intrepid-card__check-path", fill: "none",
              stroke: "currentColor", "stroke-width": "2.4", "stroke-linecap": "round",
              "stroke-linejoin": "round", d: "M4 12.5 L10 18.5 L20 6" } },
          { n: "p", c: "egt-intrepid-card__queued", "data-egt-demo": "queued", i: card.queued }
        ] },
      /* actions */
      { n: "div", c: "egt-intrepid-card__actions", "data-egt-demo": "actions",
        i: [
          { n: "button", type: "button", c: "egt-cta egt-intrepid-card__approve",
            "data-egt-demo": "approve",
            i: [ "Approve", { n: "span", c: "egt-cta__tick", i: " ›" } ] },
          { n: "button", type: "button", c: "egt-intrepid-card__dismiss",
            "data-egt-demo": "dismiss", i: "Dismiss" }
        ] },
      /* undo affordance (revealed after approve) */
      { n: "div", c: "egt-intrepid-card__undo-row", "data-egt-demo": "undoRow", "aria-hidden": "true",
        i: { n: "button", type: "button", c: "egt-intrepid-card__undo",
          "data-egt-demo": "undo", i: "Undo" } }
    ]
  };
}

/* S5 — WHO STEERS IT. The boundary / scope-clarity beat. KEPT: a calm
 * statement over imagery with a single hairline that "draws" left-to-right
 * on scroll as a quiet metaphor for the line it will not cross. */
function whoSteers() {
  return {
    n: "section", c: "egt-canvas egt-canvas--center egt-intrepid-steer",
    i: [
      S.media({ img: "/imgs/intrepid-lamp-night.jpg", alt: "", parallax: 0.06 }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-intrepid-steer__inner",
        i: { n: "div", c: "egt-glass egt-glass--wide egt-intrepid-steer__card egt-reveal egt-reveal--up",
          i: [
            { n: "p", c: "egt__eyebrow", i: "Who steers it" },
            { n: "h2", c: "egt-intrepid-steer__title", i: "You hold the pen." },
            { n: "div", c: "egt-intrepid-steer__rule", "data-egt-rule": "true", "aria-hidden": "true" },
            { n: "p", c: "egt-intrepid-steer__copy",
              i: [ "Intrepid is yours to steer — your brand, your data, your goals. And it's built with a line it will not cross: it never ",
                { n: "span", c: "egt__gold", i: "publishes, sends, or charges" },
                " on its own. Ever." ] }
          ] } }
    ]
  };
}

/* S6 — THE HONEST CLOSE + RESERVE. KEPT. Candor IS the brand. Dawn = "you
 * wake to". One frosted card, low-centered: the emotional button, then
 * reserve, then the "see the platform it lives in" secondary link. */
function comingClose(reserve) {
  return {
    n: "section", c: "egt-canvas egt-canvas--center egt-canvas--tall egt-intrepid-close",
    i: [
      S.media({ img: "/imgs/intrepid-dawn-horizon.jpg", alt: "", parallax: 0.08 }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-intrepid-close__inner",
        i: { n: "div", c: "egt-glass egt-glass--wide egt-intrepid-close__card egt-reveal egt-reveal--up",
          i: [
            { n: "p", c: "egt__eyebrow", i: "Honestly" },
            { n: "p", c: "egt-intrepid-close__line",
              i: [ "Intrepid is ", { n: "span", c: "egt__gold", i: "yours in the Beta." } ] },
            { n: "p", c: "egt-intrepid-close__sub",
              i: "Join the Beta and Intrepid's full range is yours — first through the door, holding your founding place." },
            { n: "p", c: "egt-intrepid-close__fine",
              i: "Depending on which package you choose for your public Beta, additional charges may apply." },
            { n: "div", c: "egt-intrepid-close__actions",
              i: [
                S.cta(reserve, "Join the Beta", true),
                { n: "a", href: "/platform", c: "egt-cta egt-cta--text egt-intrepid-close__secondary",
                  i: [ "See the platform it lives in", { n: "span", c: "egt-cta__tick", i: " ›" } ] }
              ] }
          ] } }
    ]
  };
}
